"use client";

import { FC, useEffect, useMemo, useRef } from "react";
import getBudgetCheckSteps from "../../lib/budgetCheck";
import { BudgetCheckFormValues } from "../../lib/budgetCheck/types";
import { FormState } from "../../lib/context/BudgetCheckContext";
import useBudgetCheckContext from "../../lib/hooks/useBudgetCheckContext";
import useFormSteps from "../../lib/hooks/useFormSteps";
import { useTranslation } from "../../lib/hooks/useTranslations";

interface BudgetCheckProps {
  hasExplicitOptIn?: boolean;
}

const BudgetCheck: FC<BudgetCheckProps> = ({ hasExplicitOptIn }) => {
  const { t } = useTranslation();
  const { state, dispatch } = useBudgetCheckContext();
  const ref = useRef<HTMLDivElement>(null);

  const onSetForm = (form: FormState<BudgetCheckFormValues>) => {
    dispatch({ type: "SET_BUDGET_CHECK_FORM_STATE", form });
  };

  const steps = useMemo(
    () => getBudgetCheckSteps(t, hasExplicitOptIn),
    [t, hasExplicitOptIn],
  );
  const formSteps = useFormSteps<BudgetCheckFormValues>({
    formSteps: steps,
    hasZeroStep: false,
    formState: state.budgetCheckFormState,
    onSetForm,
  });

  useEffect(() => {
    if (formSteps.currentStepNumber) {
      ref.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [formSteps.currentStepNumber]);

  const Component = formSteps.currentStep?.component;
  if (!Component) return null;

  return (
    <div ref={ref} className="scroll-m-[148px] m:scroll-m-5xl">
      <Component
        steps={formSteps.nextSteps}
        onPrevious={formSteps.previousStep}
        onNext={formSteps.nextStep}
        numberOfSteps={formSteps.numberOfSteps}
        hasZeroStep={false}
        currentStepNumber={formSteps.currentStepNumber}
        step={formSteps.currentStep!}
        value={formSteps.value}
        onGoBack={formSteps.goBackToStep}
      />
    </div>
  );
};

export default BudgetCheck;
